import { useState, useMemo } from "react";
import { Search, Pencil, X, Loader2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";

interface Ek3Item {
  id: string;
  code: string;
  description: string;
  conditions?: string;
}

interface Ek3Response {
  data: Ek3Item[];
}

interface Ek3ModalData {
  id?: string;
  code: string;
  description: string;
  conditions: string;
}

export function DataEk3Page() {
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<Ek3ModalData>({
    code: "",
    description: "",
    conditions: "",
  });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const { data, loading, error, refetch } = useFetch<Ek3Response>(
    "/data/ek3",
    []
  );

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (!search) return data.data;
    const q = search.toLowerCase();
    return data.data.filter(
      (item) =>
        item.code.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q)
    );
  }, [data, search]);

  function openCreateModal() {
    setModalData({ code: "", description: "", conditions: "" });
    setModalError("");
    setModalOpen(true);
  }

  function openEditModal(item: Ek3Item) {
    setModalData({
      id: item.id,
      code: item.code,
      description: item.description,
      conditions: item.conditions || "",
    });
    setModalError("");
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setModalData({ code: "", description: "", conditions: "" });
    setModalError("");
  }

  async function handleSave() {
    if (!modalData.code.trim() || !modalData.description.trim()) {
      setModalError("Kod ve tanım alanları zorunludur.");
      return;
    }

    setSaving(true);
    setModalError("");
    try {
      if (modalData.id) {
        // Edit existing
        await adminFetch(`/data/ek3/${modalData.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            description: modalData.description.trim(),
            conditions: modalData.conditions.trim() || null,
          }),
        });
      } else {
        // TODO: POST endpoint for creating new EK-3 items does not exist yet.
        // When the API is ready, uncomment the following:
        // await adminFetch("/data/ek3", {
        //   method: "POST",
        //   body: JSON.stringify({
        //     code: modalData.code.trim(),
        //     description: modalData.description.trim(),
        //     conditions: modalData.conditions.trim() || null,
        //   }),
        // });
        setModalError("Yeni EK-3 ekleme API'si henüz hazır değil.");
        setSaving(false);
        return;
      }
      closeModal();
      refetch();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Bir hata oluştu");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="EK-3 kodu veya tanım ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-full rounded-lg border border-border bg-secondary pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:w-80"
          />
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <Plus className="h-4 w-4" />
          Yeni EK-3 Ekle
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {loading ? (
          <PageLoading />
        ) : error ? (
          <ErrorMessage message={error} onRetry={refetch} className="m-4" />
        ) : filteredData.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            {search
              ? "Arama sonucu bulunamadı."
              : "Henüz EK-3 verisi bulunmuyor."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="w-28 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Kod
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Tanım
                  </th>
                  <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground md:table-cell">
                    Koşullar
                  </th>
                  <th className="w-20 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredData.map((item) => (
                  <tr
                    key={item.id}
                    className="transition-colors hover:bg-secondary/50"
                  >
                    <td className="px-4 py-3">
                      <code className="rounded bg-secondary px-1.5 py-0.5 text-sm font-mono text-accent">
                        {item.code}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">
                        {item.description}
                      </span>
                    </td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      <span className="text-sm text-muted-foreground">
                        {item.conditions || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEditModal(item)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                        aria-label="Düzenle"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/60"
            onClick={closeModal}
          />
          <div className="relative z-10 w-full max-w-lg rounded-xl border border-border/60 bg-card p-6 shadow-xl ring-1 ring-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                {modalData.id ? (
                  <>
                    EK-3 Düzenle -{" "}
                    <code className="text-accent">{modalData.code}</code>
                  </>
                ) : (
                  "Yeni EK-3 Ekle"
                )}
              </h2>
              <button
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Kapat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              {modalError && (
                <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {modalError}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Kod
                </label>
                <input
                  type="text"
                  value={modalData.code}
                  onChange={(e) =>
                    setModalData({ ...modalData, code: e.target.value })
                  }
                  disabled={!!modalData.id}
                  placeholder="Örn: 1101.1.1"
                  className={cn(
                    "h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent",
                    modalData.id && "cursor-not-allowed opacity-60"
                  )}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Tanım
                </label>
                <textarea
                  value={modalData.description}
                  onChange={(e) =>
                    setModalData({ ...modalData, description: e.target.value })
                  }
                  rows={3}
                  placeholder="EK-3 yatırım konusu tanımı"
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Koşullar
                </label>
                <textarea
                  value={modalData.conditions}
                  onChange={(e) =>
                    setModalData({ ...modalData, conditions: e.target.value })
                  }
                  rows={3}
                  placeholder="Varsa özel koşullar"
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  İptal
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                    saving && "cursor-not-allowed opacity-50"
                  )}
                >
                  {saving && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
